import React, { useEffect, useState } from 'react';
import { Plus, Search, Phone, Instagram, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatDate } from '../lib/format';

const TYPES = ['client', 'lead', 'hired_artist', 'vendor', 'outreach_target'];

export default function Contacts() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [type, setType] = useState('all');
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = () => api.get('/contacts').then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = items.filter(c =>
    (type === 'all' || c.type === type) &&
    (!q || (c.name + ' ' + c.phone + ' ' + c.city).toLowerCase().includes(q.toLowerCase()))
  );

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/contacts/${data.id}`, data);
      else await api.post('/contacts', data);
      toast.success('Contact saved');
      setOpen(false); setEdit(null); load();
    } catch { toast.error('Save failed'); }
  };

  return (
    <div className="px-5 py-5 lg:px-10 lg:py-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="eyebrow">CRM</div>
          <h1 className="serif text-[28px]">Contacts</h1>
        </div>
        <button className="btn-gold" onClick={() => { setEdit({ type: 'client' }); setOpen(true); }} data-testid="contact-add-btn">
          <Plus size={16} /> New
        </button>
      </div>

      <div className="flex gap-2 mb-4 items-center">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input placeholder="Search contacts…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" data-testid="contact-search" />
        </div>
        <select value={type} onChange={e => setType(e.target.value)} className="w-40" data-testid="contact-type-filter">
          <option value="all">All types</option>
          {TYPES.map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
        </select>
      </div>

      <div className="card-cream divide-y" style={{borderColor:'var(--line)'}}>
        {filtered.length === 0 && <div className="p-8 text-sm text-[var(--muted)] italic text-center">No contacts yet.</div>}
        {filtered.map(c => (
          <div key={c.id} className="p-4 flex items-start justify-between gap-3 hover:bg-[var(--cream-alt)] cursor-pointer" onClick={() => { setEdit(c); setOpen(true); }} data-testid={`contact-${c.id}`}>
            <div>
              <div className="serif text-[18px]">{c.name}</div>
              <div className="text-xs text-[var(--muted)] flex gap-3 mt-1 flex-wrap">
                <span className="uppercase tracking-[0.14em]">{c.type.replace('_',' ')}</span>
                {c.city && <span>{c.city}</span>}
                {c.phone && <span className="flex items-center gap-1"><Phone size={11} />{c.phone}</span>}
                {c.instagram && <span className="flex items-center gap-1"><Instagram size={11} />{c.instagram}</span>}
              </div>
            </div>
            {c.birthday && <div className="text-[10px] text-[var(--gold)] tracking-[0.14em] uppercase">BDay {formatDate(c.birthday)}</div>}
          </div>
        ))}
      </div>

      {open && <ContactForm value={edit} onClose={() => { setOpen(false); setEdit(null); }} onSave={save} />}
    </div>
  );
}

function ContactForm({ value, onClose, onSave }) {
  const [f, setF] = useState({ name: '', phone: '', email: '', instagram: '', city: '', birthday: '', anniversary: '', notes: '', type: 'client', speciality: '', standard_rate: 0, ...value });
  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/40 p-0 lg:p-6">
      <div className="w-full max-w-lg card-cream p-6 lg:rounded-md" style={{background:'var(--cream)'}}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="serif text-[22px]">{value?.id ? 'Edit Contact' : 'New Contact'}</h2>
          <button onClick={onClose} data-testid="contact-form-close"><X size={18}/></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="eyebrow">Type</label>
            <select value={f.type} onChange={e => setF({ ...f, type: e.target.value })} data-testid="contact-form-type">
              {TYPES.map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
            </select>
          </div>
          <div><label className="eyebrow">Name</label><input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="contact-form-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="eyebrow">Phone / WhatsApp</label><input value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} placeholder="+91 …" data-testid="contact-form-phone" /></div>
            <div><label className="eyebrow">City</label><input value={f.city} onChange={e => setF({ ...f, city: e.target.value })} data-testid="contact-form-city" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="eyebrow">Email</label><input value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid="contact-form-email" /></div>
            <div><label className="eyebrow">Instagram</label><input value={f.instagram} onChange={e => setF({ ...f, instagram: e.target.value })} placeholder="@…" data-testid="contact-form-instagram" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="eyebrow">Birthday</label><input type="date" value={f.birthday} onChange={e => setF({ ...f, birthday: e.target.value })} data-testid="contact-form-birthday" /></div>
            <div><label className="eyebrow">Anniversary</label><input type="date" value={f.anniversary} onChange={e => setF({ ...f, anniversary: e.target.value })} data-testid="contact-form-anniversary" /></div>
          </div>
          {f.type === 'hired_artist' && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="eyebrow">Speciality</label>
                <select value={f.speciality} onChange={e => setF({ ...f, speciality: e.target.value })} data-testid="contact-form-speciality">
                  <option value="">—</option>
                  <option value="hair_artist">Hair artist</option>
                  <option value="assistant_mua">Assistant MUA</option>
                  <option value="draper">Draper</option>
                </select>
              </div>
              <div><label className="eyebrow">Standard rate (₹)</label><input type="number" value={f.standard_rate} onChange={e => setF({ ...f, standard_rate: parseFloat(e.target.value||0) })} data-testid="contact-form-rate" /></div>
            </div>
          )}
          <div><label className="eyebrow">Notes</label><textarea rows={3} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} data-testid="contact-form-notes" /></div>
          <button className="btn-gold w-full" onClick={() => onSave(f)} data-testid="contact-form-save">Save Contact</button>
        </div>
      </div>
    </div>
  );
}
